import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play, Check, Headphones } from "lucide-react";
import { useState } from "react";
import PublicShell from "../layout/PublicShell";
import ChatMock from "../components/landing/ChatMock";
import { Button } from "../components/ui/Button";
import { fadeUp, stagger } from "../lib/motion";

const BOT_URL = `https://t.me/${import.meta.env.VITE_BOT_USERNAME || "ilmli_bot"}`;

// "Lines" are sample speech — the kind of thing each persona would
// actually say to you in chat. Closer to a voice sample than a
// description. Keeps the cards feeling like real personalities.
const PERSONAS: Array<{ id: string; name: string; line: string }> = [
  { id: "casual",       name: "Casual",            line: "morning, what's up?" },
  { id: "grumpy",       name: "Grumpy Old Timer",  line: "...again? fine, again." },
  { id: "optimist",     name: "Cheerful Optimist", line: "ooh, this is gonna be fun!" },
  { id: "philosopher",  name: "Deep Thinker",      line: "but why do we say it that way?" },
  { id: "gossip",       name: "Nosy Friend",       line: "okay tell me everything." },
  { id: "minimalist",   name: "Dry Minimalist",    line: "ready?" },
];

// Six condensed beats. Tone matches docs/in_brief.md "what it does"
// + "how it behaves" — life shapes, not engineering specifics. No
// vendor names (Anki), no acronyms (TTS), no internal taxonomy.
const USAGE_NOTES: Array<{ t: string; b: string }> = [
  {
    t: "It pings you first.",
    b: "Messages you when you're awake, in your timezone, when there's something worth saying. Reply when you can. Silence is fine.",
  },
  {
    t: "It remembers your life.",
    b: "Trips, deadlines, the people in your orbit, the topics you've asked it to leave alone. When something matters, it brings it up — a week before, the night before, the morning after.",
  },
  {
    t: "Voice both ways.",
    b: "Type a line, send a voice memo, get one back. Pacing matched to your level so you can keep up.",
  },
  {
    t: "A photo becomes a lesson.",
    b: "Snap a menu, a sign, a letter, your dinner — your tutor names what it sees and teaches you the word on the spot.",
  },
  {
    t: "Words come back before you forget them.",
    b: "The right word at the right moment. Mistakes corrected by demonstration, never by lecture. No flashcards, no quizzes.",
  },
  {
    t: "Numbers, only if you ask.",
    b: "Vocabulary, mastery, level — every detail tracked. Invisible by default, yours on demand.",
  },
];

const TIERS = [
  {
    id: "trial",
    name: "Taste",
    tagline: "try it for a week",
    price: "Free",
    period: "7 days · no card",
    features: [
      "1 language",
      "10 turns / day",
      "All six personas",
      "Text only — no voice",
    ],
    cta: { label: "Start the trial", href: "/signup", variant: "outline" as const },
    highlight: false,
  },
  {
    id: "daily",
    name: "Daily",
    tagline: "one language, every day",
    price: "$4.99",
    period: "per month · or $48/yr — 20% off",
    features: [
      "1 language",
      "50 turns / day",
      "60 voice notes / month",
      "All six personas",
      "Full dashboard + CEFR auto-advance",
      "Spaced repetition vocabulary",
    ],
    cta: { label: "Begin", href: "/signup", variant: "outline" as const },
    highlight: false,
  },
  {
    id: "trio",
    name: "Trio",
    tagline: "three at once, fluent faster",
    price: "$9.99",
    period: "per month · or $96/yr — 20% off",
    features: [
      "Up to 3 languages",
      "100 turns / day",
      "120 voice notes / month",
      "Everything in Daily",
    ],
    cta: { label: "Begin", href: "/signup", variant: "primary" as const },
    highlight: true,
  },
  {
    id: "polyglot",
    name: "Polyglot",
    tagline: "the whole shelf",
    price: "$14.99",
    period: "per month · or $144/yr — 20% off",
    features: [
      "Unlimited languages",
      "150 turns / day",
      "200 voice notes / month",
      "Design a custom personality",
      "Early access to new languages",
    ],
    cta: { label: "Begin", href: "/signup", variant: "outline" as const },
    highlight: false,
  },
];

function pronounceIlmli(): Promise<void> {
  return new Promise((resolve) => {
    const audio = new Audio("/ilmli.mp3");
    audio.onended = () => resolve();
    audio.onerror = () => resolve();
    audio.play().catch(() => resolve());
  });
}

export default function Landing() {
  const [speaking, setSpeaking] = useState(false);
  const [playingPersona, setPlayingPersona] = useState<string | null>(null);

  async function onHearIt() {
    if (speaking) return;
    setSpeaking(true);
    try { await pronounceIlmli(); } finally { setSpeaking(false); }
  }

  function playPersona(id: string) {
    if (playingPersona) return;
    setPlayingPersona(id);
    const audio = new Audio(`/personas/${id}.mp3`);
    const done = () => setPlayingPersona(null);
    audio.onended = done;
    audio.onerror = done;
    audio.play().catch(done);
  }

  return (
    <PublicShell>
      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-[1.1fr_1fr] gap-14 md:gap-20 items-start">
          <motion.div initial="hidden" animate="show" variants={stagger(0.1)} className="relative z-10">
            <motion.div variants={fadeUp} className="flex items-center gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse shrink-0" />
              <span className="font-serif italic text-ink-soft text-lg">
                the language tutor that remembers you.
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="display mt-7 text-[clamp(4rem,9vw,7.5rem)] inline-flex items-baseline text-ink leading-none"
            >
              <span
                className="inline-flex items-end"
                style={{ height: "0.85em", marginRight: "0.18em" }}
                aria-hidden
              >
                <svg viewBox="0 0 70 90" xmlns="http://www.w3.org/2000/svg" style={{ height: "100%", width: "auto" }}>
                  <path
                    d="M 12 26 C 12 12, 26 6, 42 8 C 58 10, 64 22, 60 38 C 56 54, 50 60, 46 70 C 44 78, 38 84, 30 84 C 22 84, 18 76, 18 68 C 18 54, 12 40, 12 26 Z"
                    stroke="#17171A" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"
                  />
                  <g transform="translate(20 20) scale(1.5) translate(-2 -3)">
                    <path
                      d="M3,13.5793719 C9.98914658,5.83454059 14.2442185,2.78592627 15.7652158,4.43352892 C18.0467117,6.90493289 7.55581053,16.1455344 9.47834357,17.954063 C11.4008766,19.7625917 16.9959382,11.5719148 19.0578414,12.4109285 C21.1197445,13.2499421 16.1152903,18.1722847 17.4055985,18.9997829 C18.2658039,19.5514483 19.3191667,19.0606734 20.5656867,17.527458"
                      stroke="#B8472C" strokeWidth="2.67" strokeLinecap="round" strokeLinejoin="round" fill="none"
                    />
                  </g>
                </svg>
              </span>
              <span className="italic font-medium tracking-tight">ilmli</span>
            </motion.h1>

            <motion.div variants={fadeUp} className="mt-5 flex items-center gap-3 text-ink-soft">
              <span className="text-sm tabular-nums">/ˈɪlm.liː/</span>
              <span className="w-px h-3 bg-ink-faint/40" />
              <button
                onClick={onHearIt}
                disabled={speaking}
                className="inline-flex items-center gap-1.5 text-xs hover:text-terracotta transition-colors disabled:opacity-60"
                aria-label="Play pronunciation"
              >
                <Play size={10} fill="currentColor" className={speaking ? "animate-pulse" : ""} />
                {speaking ? "speaking…" : "hear it"}
              </button>
              <span className="w-px h-3 bg-ink-faint/40" />
              <span className="italic font-serif text-sm">adj · informal</span>
            </motion.div>

            <motion.p variants={fadeUp} className="mt-4 text-sm text-ink-faint italic font-serif leading-relaxed max-w-md">
              from Arabic <span className="not-italic">علم</span> (<span className="not-italic">ilm</span>, "knowledge") + Turkish <span className="not-italic">-li</span> — "with knowledge; learned."
            </motion.p>

            <motion.ol variants={stagger(0.12)} className="mt-10 space-y-6 max-w-xl">
              <motion.li variants={fadeUp} className="grid grid-cols-[1.5rem_1fr] gap-5 items-baseline">
                <span className="font-serif text-terracotta text-[1.35rem] md:text-[1.5rem] tabular-nums">1.</span>
                <span className="font-serif text-[1.35rem] md:text-[1.5rem] leading-snug">
                  to learn a language by simply <span className="italic-accent">talking</span> to it — a few lines a day, in a messenger you already use.
                </span>
              </motion.li>
              <motion.li variants={fadeUp} className="grid grid-cols-[1.5rem_1fr] gap-5 items-baseline">
                <span className="font-serif text-terracotta text-[1.35rem] md:text-[1.5rem] tabular-nums">2.</span>
                <span className="font-serif text-[1.35rem] md:text-[1.5rem] leading-snug">
                  <span className="italic text-ink-soft">(proper noun)</span>&nbsp;a tutor with a <span className="italic-accent">memory</span> — knows what you told it last week, brings it up when it matters, slips new words into every reply.
                </span>
              </motion.li>
            </motion.ol>

            <motion.div variants={fadeUp} className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <Link to="/signup">
                <Button size="lg">
                  Start free — 7 days, no card <ArrowRight size={16} />
                </Button>
              </Link>
              <a
                href="#how"
                className="text-ink-soft text-sm underline decoration-ink-faint/40 underline-offset-4 hover:decoration-terracotta hover:text-terracotta transition-colors"
              >
                or watch the 30-second demo
              </a>
            </motion.div>
            <motion.div
              variants={fadeUp}
              className="mt-4 font-mono text-[12px] text-ink-faint tracking-wide"
            >
              2 minutes to set up · live in Telegram · works on any phone
            </motion.div>
          </motion.div>

          <div className="flex justify-center md:justify-end">
            <ChatMock />
          </div>
        </div>
      </section>

      {/* ─── METHOD: SANDWICH VISUALIZATION ───────────────────────── */}
      {/* id="how" is the anchor target for the "How it works" nav link
          in PublicShell — the sandwich section IS the how-it-works
          explanation, no need for a separate block. */}
      <section id="how" className="border-t border-[rgba(42,36,26,0.12)] bg-paper-soft/40">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36 grid md:grid-cols-[1fr_1.1fr] gap-14 md:gap-20 items-center">
          <div>
            <div className="font-serif italic text-ink-soft text-lg mb-4">the sandwich method</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] leading-tight">
              Native bread, <span className="italic-accent">foreign</span> filling.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-md">
              Your tutor starts each sentence in your native language, slips one or two target-language words in the middle — with a small gloss — and closes in your native again.
            </p>
            <p className="mt-4 text-ink-soft leading-relaxed max-w-md">
              As your pace quickens, the bread thins. Foreign words carry more weight, glosses fade, and one morning you realize the whole sandwich came out in the target language — and you understood it.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6 font-serif"
          >
            {/* Week 1 */}
            <div>
              <div className="text-[0.68rem] tracking-[0.16em] uppercase text-ink-faint mb-2">Week 1</div>
              <p className="text-xl md:text-2xl leading-relaxed">
                <span>Yesterday I had </span>
                <span className="italic-accent font-medium">Feierabend</span>
                <span className="text-ink-faint text-base italic"> (cozy end-of-workday) </span>
                <span>on the sofa with coffee.</span>
              </p>
            </div>
            {/* Week 4 */}
            <div className="pl-6 border-l-2 border-terracotta/40">
              <div className="text-[0.68rem] tracking-[0.16em] uppercase text-ink-faint mb-2">Week 4</div>
              <p className="text-xl md:text-2xl leading-relaxed">
                <span>Gestern war mein </span>
                <span className="italic-accent font-medium">Feierabend</span>
                <span> mit Kaffee </span>
                <span className="text-ink-soft">on the sofa.</span>
              </p>
            </div>
            {/* Week 12 */}
            <div className="pl-6 border-l-2 border-terracotta">
              <div className="text-[0.68rem] tracking-[0.16em] uppercase text-ink-faint mb-2">Week 12</div>
              <p className="text-xl md:text-2xl leading-relaxed">
                <span>Gestern hatte ich </span>
                <span className="italic-accent font-medium">Feierabend</span>
                <span> auf dem Sofa mit Kaffee.</span>
              </p>
              <p className="text-sm text-ink-faint italic mt-2">— and you didn't think twice.</p>
            </div>
          </motion.div>
        </div>

      </section>

      {/* ─── THE MOMENT ───────────────────────────────────────────── */}
      <section className="border-t border-[rgba(42,36,26,0.12)]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="max-w-2xl mb-14">
            <div className="font-serif italic text-ink-soft text-lg mb-4">the moment</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] leading-tight">
              What you're actually <span className="italic-accent">buying</span>.
            </h2>
          </div>
          <div className="max-w-2xl font-serif text-xl md:text-2xl leading-relaxed text-ink space-y-5">
            <p className="m-0">Tuesday afternoon. Your phone buzzes.</p>
            <p className="m-0 italic text-ink-soft">
              "trip's in 12 days — want to run through ordering food once tonight, just to keep it warm?"
            </p>
            <p className="m-0">
              You hadn't thought about it in two weeks. You actually do want to. Five minutes later
              you put the phone down. You've practiced the thing you'd otherwise never have practiced.
            </p>
            <p className="m-0">
              It remembers what you said. It'll bring it up again at the right moment.
            </p>
          </div>
          <p className="mt-10 max-w-2xl font-serif italic text-ink-faint text-lg md:text-xl">
            That moment is the product. Everything else is in service of it.
          </p>
        </div>
      </section>

      {/* ─── ANTI-PITCH ───────────────────────────────────────────── */}
      <section className="border-t border-[rgba(42,36,26,0.12)] bg-paper-soft/40">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="max-w-2xl mb-14">
            <div className="font-serif italic text-ink-soft text-lg mb-4">what you'll never see</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] leading-tight">
              The <span className="italic-accent">shortest</span> pitch ever.
            </h2>
          </div>
          <p className="max-w-3xl font-serif text-ink text-xl md:text-2xl leading-snug m-0">
            No streaks. No quizzes. No XP. No leaderboards. No cards. No hearts. No owls glaring at you.
            No push notifications about how many days you've missed.
          </p>
          <p className="mt-8 max-w-2xl font-serif italic text-ink-soft text-lg md:text-xl leading-snug m-0">
            We will never guilt you into opening the app. Because there is no app.
          </p>
        </div>
      </section>

      {/* ─── USAGE — consolidated: how-it-works + agentic + features + progress ── */}
      <section id="usage" className="border-t border-[rgba(42,36,26,0.12)]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-32">
          <div className="max-w-2xl mb-14">
            <div className="font-serif italic text-ink-soft text-lg mb-4">usage · how it shows up in your day</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] leading-tight">
              Quiet by default. <span className="italic-accent">Loud</span> only when it matters.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-xl">
              Your tutor pings when you're awake, answers with your real numbers,
              slips new words in where you'll meet them, and stays out of the way
              when you don't.
            </p>
          </div>

          <motion.ol
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger(0.06)}
            className="grid md:grid-cols-2 gap-x-14 lg:gap-x-20 gap-y-10"
          >
            {USAGE_NOTES.map((u) => (
              <motion.li
                key={u.t}
                variants={fadeUp}
                className="grid grid-cols-[1.5rem_1fr] gap-4 items-baseline"
              >
                <span className="font-serif italic text-terracotta text-xl leading-none">▸</span>
                <div>
                  <h3 className="font-serif text-2xl md:text-[1.65rem] leading-snug">{u.t}</h3>
                  <p className="mt-2 text-ink-soft text-[0.95rem] leading-relaxed">{u.b}</p>
                </div>
              </motion.li>
            ))}
          </motion.ol>

          <p className="mt-14 max-w-xl font-serif italic text-ink-soft text-base leading-relaxed">
            <span className="italic-accent">▸</span> see also:{" "}
            <a href="#personas" className="underline decoration-ink-faint/40 hover:decoration-terracotta">personas</a>,{" "}
            <a href="#pricing" className="underline decoration-ink-faint/40 hover:decoration-terracotta">pricing</a>.
          </p>
        </div>
      </section>

      {/* ─── PERSONAS ─────────────────────────────────────────────── */}
      <section id="personas" className="border-t border-[rgba(42,36,26,0.12)] bg-paper-soft/40">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="max-w-2xl mb-20">
            <div className="font-serif italic text-ink-soft text-lg mb-4">personalities</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] leading-tight">
              Pick a personality you'd <span className="italic-accent">actually</span> speak to.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-xl">
              Six distinct personalities. Same curriculum, different mood. Switch anytime — your progress follows you.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger(0.06)}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8"
          >
            {PERSONAS.map((p, i) => (
              <motion.button
                key={p.id}
                variants={fadeUp}
                onClick={() => playPersona(p.id)}
                className="flex items-baseline gap-6 pb-6 border-b border-[rgba(42,36,26,0.12)] text-left hover:border-terracotta transition-colors group w-full"
              >
                <span className="font-serif italic text-terracotta text-lg tabular-nums shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <div className="font-serif text-2xl md:text-[1.65rem] leading-tight">{p.name}</div>
                  <div className="text-ink-soft text-sm mt-1">{p.line}</div>
                </div>
                <Play
                  size={14}
                  fill="currentColor"
                  className={`text-ink-faint group-hover:text-terracotta shrink-0 transition-colors ${
                    playingPersona === p.id ? "text-terracotta animate-pulse" : ""
                  }`}
                />
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── LANGUAGES ────────────────────────────────────────────── */}
      {/* id="languages" is the anchor target for the "Languages" nav
          link. Keep this deliberately small — enough to answer "do you
          have my language?" without pretending each has equal depth
          (German is the flagship; the rest ship in tiers). */}
      <section id="languages" className="border-t border-[rgba(42,36,26,0.12)]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="max-w-2xl mb-10">
            <div className="font-serif italic text-ink-soft text-lg mb-4">languages</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] leading-tight">
              Twelve to learn. <span className="italic-accent">More coming.</span>
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-lg">
              German is the flagship — deepest curriculum, richest personas. The rest are
              live and improving. You can pick your native from any of the twelve too, so
              glosses land in the language you actually think in.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {[
              { code: "de", name: "German",    endonym: "Deutsch",     flag: "flagship" },
              { code: "es", name: "Spanish",   endonym: "Español" },
              { code: "fr", name: "French",    endonym: "Français" },
              { code: "it", name: "Italian",   endonym: "Italiano" },
              { code: "pt", name: "Portuguese", endonym: "Português" },
              { code: "nl", name: "Dutch",     endonym: "Nederlands" },
              { code: "ru", name: "Russian",   endonym: "Русский" },
              { code: "ja", name: "Japanese",  endonym: "日本語" },
              { code: "zh", name: "Mandarin",  endonym: "中文" },
              { code: "ko", name: "Korean",    endonym: "한국어" },
              { code: "ar", name: "Arabic",    endonym: "العربية" },
              { code: "en", name: "English",   endonym: "English" },
            ].map((l) => (
              <span
                key={l.code}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[13px] ${
                  l.flag
                    ? "border-terracotta bg-terracotta/10 text-ink"
                    : "border-[rgba(42,36,26,0.18)] text-ink-soft"
                }`}
                title={l.endonym !== l.name ? l.endonym : undefined}
              >
                <span className="font-medium">{l.name}</span>
                {l.endonym && l.endonym !== l.name && (
                  <span className="text-ink-faint text-xs">{l.endonym}</span>
                )}
                {l.flag && (
                  <span className="font-mono text-[10px] tracking-wide uppercase text-terracotta ml-1">
                    {l.flag}
                  </span>
                )}
              </span>
            ))}
          </div>

          <p className="font-serif italic text-ink-faint text-[14px]">
            Missing yours? <a href="mailto:hello@ilmli.ai" className="text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta">Tell us</a> — we keep a list.
          </p>
        </div>
      </section>

      {/* ─── PRICING ──────────────────────────────────────────────── */}
      <section id="pricing" className="border-t border-[rgba(42,36,26,0.12)] bg-paper-soft/40">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="max-w-2xl mb-16">
            <div className="font-serif italic text-ink-soft text-lg mb-4">pricing</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)]">
              Simple <span className="italic-accent">tiers</span>. No surprises.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-lg">
              A turn = one message you send + one reply. Turns reset every day; voice notes pool monthly so you can binge on a Saturday and chat on Tuesday.
              Cancel anytime, one click, 14-day refund on paid plans.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((t) => (
              <div
                key={t.id}
                className={`relative rounded-lg p-8 flex flex-col ${
                  t.highlight
                    ? "bg-ink-card text-paper shadow-float"
                    : "bg-paper border border-[rgba(42,36,26,0.15)]"
                }`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-8 px-2.5 py-0.5 bg-terracotta text-paper text-[0.65rem] tracking-[0.16em] uppercase rounded">
                    recommended
                  </div>
                )}
                <div>
                  <div className="font-serif text-3xl">{t.name}</div>
                  <div className={`text-sm mt-1 italic ${t.highlight ? "text-paper/60" : "text-ink-faint"}`}>
                    {t.tagline}
                  </div>
                </div>
                <div className="mt-8 flex items-baseline gap-2">
                  <span className="font-serif text-6xl leading-none">{t.price}</span>
                </div>
                <div className={`text-xs mt-2 ${t.highlight ? "text-paper/50" : "text-ink-faint"}`}>{t.period}</div>

                <ul className="mt-8 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-sm ${t.highlight ? "text-paper/80" : "text-ink-soft"}`}>
                      <Check size={14} className={`mt-0.5 shrink-0 ${t.highlight ? "text-terracotta-soft" : "text-terracotta"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <Link to={t.cta.href} className="block">
                    <Button
                      variant={t.cta.variant}
                      className={`w-full justify-center ${t.highlight ? "bg-paper text-ink hover:bg-paper hover:opacity-90" : ""}`}
                    >
                      {t.cta.label} <ArrowRight size={14} />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-2xl text-sm text-ink-faint italic font-serif leading-relaxed">
            A "voice note" is a TTS reply your tutor can speak, or a transcribed voice
            memo from you — counts as both a turn and a voice credit. Each clip caps at
            30 seconds (short bursts are how people actually learn). Hit your daily
            turn cap and the bot pauses until tomorrow; hit your monthly voice cap and it
            falls back to text until the cycle rolls.
          </p>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────── */}
      <section className="border-t border-[rgba(42,36,26,0.12)]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="mb-14">
            <div className="font-serif italic text-ink-soft text-lg mb-4">frequently · honestly</div>
            <h2 className="display text-[clamp(2.2rem,4.5vw,3.75rem)] max-w-2xl">
              The questions we <span className="italic-accent">actually</span> get.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 lg:gap-x-20 gap-y-10">
            {[
              { q: "Which languages?", a: "German is live today. Spanish and French ship next. Italian, Portuguese, and Polish are on deck. If you want one we don't have, reply to any email — we keep a list." },
              { q: "Will this work if I'm a complete beginner?", a: "Yes. We start in your native language, slip target-language words in one at a time, and within a week you're reading half-and-half. Within a month, mostly target. The tutor calibrates to you, not the other way around." },
              { q: "WhatsApp instead of Telegram?", a: "Soon — WhatsApp Business API has constraints we're working around. Telegram first because it lets us be ourselves: voice notes, formatting, no bot-shaped jail." },
              { q: "What about privacy?", a: "Your messages are yours. We don't train models on them, we don't sell anything, and you can export your whole vocabulary as a CSV any time. The legalese version lives on /privacy and is, for once, readable." },
              { q: "Can I cancel any time?", a: "Yes. One click. No \"are you sure\" with five sub-screens. Your vocabulary stays in your account; come back any time and pick up." },
              { q: "What if I'm bad at this?", a: "Then we'll be bad at this together. The tutor never says \"wrong\" — they say it back the right way. After a week of saying it back, you'll start saying it right yourself. That's the whole trick." },
            ].map((f) => (
              <div key={f.q}>
                <h3 className="font-serif italic text-terracotta text-2xl md:text-[1.65rem] leading-snug">{f.q}</h3>
                <p className="mt-3 text-ink-soft text-[0.95rem] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-16 font-serif italic text-ink-soft text-base">
            Something we missed?{" "}
            <a href="mailto:hello@ilmli.ai" className="text-ink underline decoration-ink-faint/40 hover:decoration-terracotta">
              hello@ilmli.ai
            </a>
            {" "}— a real person reads everything.
          </p>
        </div>
      </section>

      {/* ─── CLOSING CTA ──────────────────────────────────────────── */}
      <section className="border-t border-[rgba(42,36,26,0.12)]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="display text-[clamp(2.5rem,5.5vw,4.25rem)] leading-tight">
            Say <span className="italic-accent">hallo</span> today.
          </h2>
          <p className="mt-6 text-ink-soft max-w-md mx-auto font-serif italic text-lg md:text-xl">
            You don't need another app. You need someone who's paying attention.
          </p>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/signup">
              <Button size="lg">Start free <ArrowRight size={16} /></Button>
            </Link>
            <a href={BOT_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline">
                <Headphones size={16} /> Already signed up? Open Telegram
              </Button>
            </a>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
